import {
  CustomPdfScript,
  EditablePdfButtonType,
  type EditablePdfButtonGroup,
} from '@urbanx/agx-ui-components';
import type {
  PdfAutomationFieldGroup,
  PdfAutomationFieldPlacement,
  PdfAutomationFieldType,
} from '@urbanx/agx.ui-components2';

const sellerDisclosureCenters: Record<string, { x: number; y: number }> = {
  SDBuyerSignatures1SignHere: { x: 125, y: 365 },
  SDBuyerSignatures1Name: { x: 125, y: 425 },
  SDBuyerSignatures1DateSigned: { x: 125, y: 485 },
  SDBuyerSignatures2SignHere: { x: 395, y: 365 },
  SDBuyerSignatures2Name: { x: 395, y: 425 },
  SDBuyerSignatures2DateSigned: { x: 395, y: 485 },
  SDBuyerSignatures3SignHere: { x: 125, y: 565 },
  SDBuyerSignatures3Name: { x: 125, y: 625 },
  SDBuyerSignatures3DateSigned: { x: 125, y: 685 },
  SDBuyerSignatures4SignHere: { x: 395, y: 565 },
  SDBuyerSignatures4Name: { x: 395, y: 625 },
  SDBuyerSignatures4DateSigned: { x: 395, y: 685 },
};

const toAutomationType = (fieldType: EditablePdfButtonType): PdfAutomationFieldType => {
  switch (fieldType) {
    case EditablePdfButtonType.Text:
    case EditablePdfButtonType.Signature:
    case EditablePdfButtonType.SignatureAndDate:
    case EditablePdfButtonType.Initial:
      return fieldType;
    case EditablePdfButtonType.Checkbox:
      throw new Error('PDF automation field type Checkbox is not supported.');
  }

  fieldType satisfies never;
};

const makeSellerDisclosurePlacements = (
  fieldGroups: PdfAutomationFieldGroup[]
): PdfAutomationFieldPlacement[] =>
  fieldGroups.flatMap((group) =>
    group.fields.flatMap((field) => {
      const center = sellerDisclosureCenters[field.name];

      if (!center) return [];

      const width = field.type === 'Initial' ? 40 : 180;
      const height = 40;

      return [
        {
          fieldName: field.name,
          pageIndex: 6,
          x: center.x - width / 2,
          y: center.y,
          width,
          height,
          referencePageWidth: 595,
          referencePageHeight: 842,
        },
      ];
    })
  );

export const makeAutomationConfiguration = (
  buttonGroups: EditablePdfButtonGroup[],
  customScript: CustomPdfScript
): {
  fieldGroups: PdfAutomationFieldGroup[];
  placements: PdfAutomationFieldPlacement[];
} => {
  const fieldGroups = buttonGroups.map((group) => ({
    fields: group.buttons.map((button) => ({
      label: button.buttonName,
      name: button.pdfFieldName,
      type: toAutomationType(button.pdfFieldType),
    })),
  }));

  switch (customScript) {
    case CustomPdfScript.None:
      return { fieldGroups, placements: [] };
    case CustomPdfScript.SellersDisclosureFields:
      return { fieldGroups, placements: makeSellerDisclosurePlacements(fieldGroups) };
  }

  customScript satisfies never;
};
